import { useLocation, useNavigate, useParams } from "react-router-dom";
import { GoArrowLeft } from "react-icons/go";
import { MdOpenInNew } from "react-icons/md";
import {
  AssistantAllButtonTypes,
  CodeFile,
  languageType,
} from "utils/interfaces";
import AssistantPopup from "components/Editor/assistant/AssistantPopup";
import { usePlatform } from "components/utils/PlatformContext";

interface ExternalEditorHeaderProps {
  name: string;
  language: languageType | null;
  AIAssistant: {
    name: string;
    img: string;
    greeting: string;
    intro: string;
  };
  activeItem: "editor" | "assistant";
  setActiveItem: React.Dispatch<React.SetStateAction<"assistant" | "editor">>;
  showAssistant: boolean;
  assistantPopupState:
    | "closed"
    | "popup"
    | "popupNoCode"
    | "expanded"
    | "fullscreen";
  setAssistantPopupState: React.Dispatch<
    React.SetStateAction<
      "closed" | "popup" | "popupNoCode" | "expanded" | "fullscreen"
    >
  >;
  assistantPopupClickedButton: AssistantAllButtonTypes;
  setAssistantPopupClickedButton: React.Dispatch<
    React.SetStateAction<AssistantAllButtonTypes>
  >;
  codeFiles: CodeFile[];
  showExternalLink: boolean;
}

const ExternalEditorHeader: React.FC<ExternalEditorHeaderProps> = ({
  name,
  language,
  AIAssistant,
  activeItem,
  setActiveItem,
  showAssistant,
  assistantPopupState,
  setAssistantPopupState,
  assistantPopupClickedButton,
  setAssistantPopupClickedButton,
  codeFiles,
  showExternalLink,
}) => {
  // Get the platform context
  const { logo } = usePlatform();

  // Define a navigator state
  const navigate = useNavigate();

  // Get the parameters from the url
  const { moduleOrProject } = useParams();
  // Get a state if we are receiving a state variable from another url
  const { state } = useLocation();

  // Return to home url
  const goToDashboard = () => {
    if (state) {
      navigate("/home", {
        state: {
          // State for SuperAdmin.tsx
          activeItem: "lessons",
          // State for Lessons.tsx
          activeLesson: moduleOrProject,
          navRegister: state ? state.navRegister : [],
        },
      });
    } else {
      navigate("/home");
    }
  };

  /**
   * Function that defines the contextual behaviour of the back button
   */
  const handleClickBack = () => {
    if (activeItem === "assistant") setActiveItem("editor");
    else if (activeItem === "editor") goToDashboard();
  };

  return (
    <>
      <div className="w-full h-[155px] px-10 relative flex items-center z-20">
        {/* Logo and back button */}
        <div className="min-w-[480px] left-10 flex items-center gap-8">
          <div className="w-[124px] h-16 flex items-center justify-center">
            <img
              className={
                "w-auto h-auto max-w-full max-h-full object-contain cursor-pointer"
              }
              src={logo}
              onClick={goToDashboard}
              alt="Platform logo"
            />
          </div>
          <div className="w-px self-stretch bg-neutral-200" />
          <div className="w-full justify-start items-center gap-6 flex">
            <div className="flex-col justify-start items-center gap-2 inline-flex">
              <div
                className="w-12 h-12 p-3 bg-neutral-50 hover:bg-neutral-100 ease-in-out duration-150 rounded-full justify-center items-center inline-flex cursor-pointer z-10"
                onClick={handleClickBack}
              >
                <div className="w-6 h-6 relative">
                  <GoArrowLeft className="w-full h-full fill-secondary-600" />
                </div>
              </div>
              <div className="justify-start items-start gap-2 inline-flex">
                <div className="text-center text-secondary-600 text-xs font-semibold font-sans uppercase leading-[14px] tracking-wide select-none">
                  Back
                </div>
              </div>
            </div>
            <h1 className="text-center text-neutral-600 text-[23px] font-bold font-sans leading-7">
              {activeItem === "assistant" ? "Feedback" : name}
            </h1>
          </div>
        </div>

        {/* Title */}
        <div className="flex-grow flex flex-col justify-center items-center">
          {activeItem === "assistant" && (
            <div className="text-center text-neutral-500 text-base font-normal font-sans leading-[19px]">
              {`Tutoring with ${AIAssistant.name}`}
            </div>
          )}
        </div>

        {/* Assistant and video buttons */}
        <div className="right-10 flex items-center mr-3 z-10">
          {/* Conditional rendering for Assistant */}
          {showAssistant && (
            <AssistantPopup
              AIAssistant={AIAssistant}
              state={assistantPopupState}
              setState={setAssistantPopupState}
              clickedButton={assistantPopupClickedButton}
              setClickedButton={setAssistantPopupClickedButton}
              codeFiles={codeFiles}
              goToDashboard={goToDashboard}
              language={language}
            />
          )}
        </div>
        {showExternalLink && (
          <div className="h-fit p-3 rounded-full justify-center items-center gap-1 inline-flex">
            <a
              target="_blank"
              href={
                language === "Minecraft"
                  ? "https://education.minecraft.net/en-us/get-started/download"
                  : language === "Micro:bit"
                  ? "https://makecode.microbit.org/"
                  : ""
              }
              className="flex-col justify-center items-center gap-2 inline-flex"
            >
              <div className="p-3 bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-150 rounded-full justify-center items-center gap-2 inline-flex cursor-pointer">
                <div className="w-6 h-6 relative">
                  <MdOpenInNew className="w-full h-full fill-white" />
                </div>
              </div>
              <div className="text-center text-secondary-600 text-xs font-semibold font-poppins uppercase leading-[14px] tracking-wide select-none">
                Open {language}
              </div>
            </a>
          </div>
        )}
      </div>
    </>
  );
};

export default ExternalEditorHeader;
